import React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import Button from '@roo/roo/Button';
import Modal from '@roo/roo/Modal';

import { failToast, warningToast, successToast } from '@utils/toast';
import { isInApp, isUniversalWindowsXP, runtimeType } from '@utils/getEnv';
import * as Ids from '@utils/ids';
import * as LX from '@utils/lxWrapper';
import * as OrderPrintService from '@utils/print/orderPrintService';
import * as orderReport from '@utils/report/orderArrivalRateReport';
import { CUSTOM_METRICS, reportCustomMetrics } from '@utils/owlReporter';

import OrderListContext from '../OrderList/OrderListContext';
import * as API from '../../api';
import {
    OrderDetailInfo,
    OrderStatusCode,
    SelfServiceCode,
    UnprocessedOrderType,
} from '../../types';

/**
 *  *  - 接单/接单并打印
 *      - 打印
 *      - 通知父组件移除此订单
 *      - 协议签署弹框
 *      - 订单到达率上报
 */

type AcceptOrderButtonProps = {
    order: OrderDetailInfo;
    /** 是否接单后自动打印 */
    autoPrint?: boolean;
    disabled: boolean;
    changeDisableStatus: (status: boolean) => void;
    onNewOrderAccepted: () => void;
};

const openSignProtocolModal = (type: string) => {
    Modal.confirm({
        title: '发配送失败',
        closable: true,
        cancelText: '我知道了',
        confirmText: '去看看',
        onConfirm: () => {
            // eslint-disable-next-line waimaie/no-direct-change-parent-location
            parent.location.href = '/promise';
        },
        children: (
            <div>{`由于您未在7天内确认${type}配送补充协议，已无法再使用${type}配送服务，请尽快前往处理`}</div>
        ),
    });
};

const { useContext, useCallback } = React;

const AcceptOrderButton: React.FC<AcceptOrderButtonProps> = ({
    order,
    autoPrint,
    disabled,
    changeDisableStatus,
    onNewOrderAccepted,
}) => {
    const orderList = useContext(OrderListContext);

    const performConfirmOrder = useCallback(async () => {
        changeDisableStatus(true);
        try {
            const response =
                (await API.confirmOrder({
                    wmPoiId: order.orderInfo.basicVo.wmPoiId,
                    orderId: order.orderInfo.wmOrderViewId,
                    acctId: Ids.getAcctId(),
                    token: Ids.getToken(),
                    isPrint: autoPrint ? 1 : 0,
                    appType: '3',
                    isAutoAccept: 0,
                })) || {};

            const data = response.data || {};

            // 优先弹配送相关tips，保留历史逻辑
            if (data.distribute_tips) {
                warningToast(data.distribute_tips);
            }

            reportCustomMetrics(
                CUSTOM_METRICS.CONFIRM_ORDER.KEY,
                1,
                {
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.HTTP_SUCCESS]: 1,
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.OPERATION_TYPE]: 'manual',
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.BUSINESS_CODE]: response.code,
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.BUSINESS_TYPE]: 'wm',
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.RUNTIME]: runtimeType,
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.IS_XP]: isUniversalWindowsXP ? 1 : 0,
                },
                {
                    acctId: Ids.getAcctId(),
                    wmPoiId: Ids.getWmPoiId(),
                },
            );

            if (response.code === 0) {
                if (autoPrint) {
                    OrderPrintService.printOrderWhenConfirm({
                        wmPoiId: order.orderInfo.basicVo.wmPoiId,
                        wmOrderViewId: order.orderInfo.wmOrderViewId,
                        businessType: order.businessType,
                    });
                }
                setTimeout(() => {
                    orderList.handleOrderStateChange({
                        type: UnprocessedOrderType.NewOrder,
                        orderViewId: order.orderInfo.wmOrderViewId,
                    });
                }, 1000);

                successToast('处理成功');
                onNewOrderAccepted();
            } else {
                if (response.code === 10031) {
                    openSignProtocolModal('美团跑腿');
                } else if (response.code === 10032) {
                    // 混合送签协议
                    openSignProtocolModal('混合');
                } else if (response.code === 9 || data.status === OrderStatusCode.CANCELED) {
                    // 订单已取消 移除当前订单
                    orderList.handleOrderStateChange({
                        type: UnprocessedOrderType.NewOrder,
                        orderViewId: order.orderInfo.wmOrderViewId,
                    });
                } else {
                    warningToast(response.msg);
                }
            }

            changeDisableStatus(false);
        } catch (e) {
            reportCustomMetrics(
                CUSTOM_METRICS.CONFIRM_ORDER.KEY,
                1,
                {
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.HTTP_SUCCESS]: 0,
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.OPERATION_TYPE]: 'manual',
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.BUSINESS_TYPE]: 'wm',
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.RUNTIME]: runtimeType,
                    [CUSTOM_METRICS.CONFIRM_ORDER.TAGS.IS_XP]: isUniversalWindowsXP ? 1 : 0,
                },
                {
                    acctId: Ids.getAcctId(),
                    wmPoiId: Ids.getWmPoiId(),
                },
            );
            failToast('操作失败，请重试');
            changeDisableStatus(false);
        }

        LX.reportMC('b_waimai_e_22bdyorq_mc', 'c_waimai_e_ilerznb2');
        orderReport.reportEvent(orderReport.ORDER_OPERATE_EVENT.MANUAL_ACCEPT, [
            order.orderInfo.wmOrderViewId,
        ]);
    }, [order, orderList, changeDisableStatus, onNewOrderAccepted, autoPrint]);

    const confirmOrder = useCallback(() => {
        performConfirmOrder();
    }, [performConfirmOrder, order.orderInfo.wmOrderViewId]);

    return order.orderInfo.isShowConfirmAndCancelButton &&
        order.orderInfo.selfService !== SelfServiceCode.GRPS &&
        (!autoPrint || isInApp) ? (
        <Button size="small" type="brand" disabled={disabled} onClick={confirmOrder}>
            {autoPrint ? '接单并打印' : <>&nbsp;&nbsp;接单&nbsp;&nbsp;</>}
        </Button>
    ) : null;
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    onNewOrderAccepted: () => dispatch({ type: 'wmUnifiedOrderData/newOrderAccepted' }),
});

export default connect(null, mapDispatchToProps)(AcceptOrderButton);
export { openSignProtocolModal };
